import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import {
  LoadCustomerBlightRights,
  RequestBlightCropFieldsByDateRange,
} from '@next/arable-lib/blight';
import {
  ApplySavedLayers,
  GeoService,
  GeoServiceInjector,
  LayerMenuItem,
  MapColors,
  SetPopupEnabledStatus,
} from '@next/arable-lib/geo';
import { buildBlightRequestedDates, defaultBlightDayRange } from '@next/arable-lib/shared';
import { PageSidesheetActions, SelectItem } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { CropFieldType, SiteState } from 'src/app/shared/models';

import { Environment } from 'src/environments/environment.model';

import { InteractionType } from '../geo/models';
import {
  AddOrEditCropField,
  CloneSelectedCropField,
  CombineFields,
  CreateSubsequentCultivation,
  DeleteCropFields,
  DivideField,
  EditMachiningDepth,
  EditSelectedCropFields,
  OpenSoilAnalysesReportForSelectedCropFields,
  QuickEditCropFields,
  SendSelectedCropFields,
} from './store/actions/crop-field.actions';
import {
  DeselectAllFields,
  DeselectInteractionFeatures,
  InteractionFeatureModified,
} from '../geo/store/actions/feature.actions';
import {
  SetBlightLayerEnabled,
  SetCropFieldsEnabled,
  SetInteractionType,
  SetRvoEnabled,
  SetSoilAnalysesEnabled,
} from '../geo/store/actions/map-context.actions';
import {
  getSelectedFeaturesLength,
  hasSelectedFeatures,
  hasSelectedMultipleFeatures,
  hasSelectedSingleFeature,
  hasSelectedTwoFeatures,
} from '../geo/store/selectors/feature.selectors';
import {
  getBlightLayerEnabled,
  getCropFieldsEnabled,
  getInteractionType,
  getRvoEnabled,
  getSoilAnalysesEnabled,
  isCloningRvo,
  isEditing,
  isModifying,
  isSelectingCropField,
  isSelectingSoilAnalysis,
} from '../geo/store/selectors/map-context.selectors';

import * as SoilAnalysisActions from '../soil-analysis/store/soil-analysis.actions';
import {
  OpenOverview,
  OpenFmsDiagnose,
  ResetCropFieldType,
  ResetCropYear,
  ShowCopyFieldsToManureSpaceDialog,
  ToggleCustomerProgressAdvice,
  ToggleCustomerProgressManure,
  ViewSalesPotential,
} from '../store/actions';

import {
  getAvailableCropYears,
  getCropFieldType,
  getCropYear,
  selectCustomerProgress,
} from '../store/selectors';
import { hasSelectedCropFieldsParticipatingInBem } from './store/selectors/crop-field.selectors';

import { OpenNvgConfirmationDialog } from '../update-nvg/store/actions';

enum ButtonAction {
  addOrEdit = 'addOrEdit',
  edit = 'edit',
  divide = 'divide',
  remove = 'remove',
  clone = 'clone',
  overview = 'overview',
  cropFieldList = 'cropFieldList',
  subsequentCultivation = 'subsequentCultivation',
  combine = 'combineFields',
  soilAnalysesReport = 'soilAnalysesReport',
  saveModification = 'saveModification',
  editMachiningDepth = 'editMachiningDepth',
  copyFieldsFromCultivationPlan = 'copyFieldsFromCultivationPlan',
  updateNvg = 'updateNvg',
  viewSalesPotential = 'viewSalesPotential',
  fmsDiagnose = 'fmsDiagnose',
}

enum SelectAction {
  cropFieldType = 'cropFieldType',
  year = 'year',
}

@Component({
  selector: 'app-registration',
  templateUrl: './registration.component.html',
  styleUrls: ['./registration.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RegistrationComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly geoService = inject<GeoService>(GeoServiceInjector);

  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;
  isEditing$ = this.store.pipe(select(isEditing));
  isModifying$ = this.store.pipe(select(isModifying));
  hasSelectedField$ = this.store.pipe(select(hasSelectedFeatures));
  hasSelectedSingleField$ = this.store.pipe(select(hasSelectedSingleFeature));
  hasSelectedTwoFields$ = this.store.pipe(select(hasSelectedTwoFeatures));
  hasSelectedMultipleFields$ = this.store.pipe(select(hasSelectedMultipleFeatures));
  hasSelectedCropFieldsParticipatingInBem$ = this.store.pipe(
    select(hasSelectedCropFieldsParticipatingInBem),
  );
  isMobile$ = this.breakpoint.ltLg$;
  primaryAction$ = combineLatest([this.hasSelectedField$, this.isEditing$, this.isModifying$]);
  cropFieldType$ = this.store.pipe(select(getCropFieldType));
  customerProgress$ = this.store.pipe(select(selectCustomerProgress));
  hasSecondaryAction$ = combineLatest([
    this.hasSelectedField$,
    this.isEditing$,
    this.isModifying$,
    this.customerProgress$,
  ]).pipe(
    map(
      ([hasSelectedField, editing, modifying, customerProgress]) =>
        !modifying && !editing && !hasSelectedField && customerProgress !== undefined,
    ),
  );
  primaryActionIcon$ = this.primaryAction$.pipe(
    map(([hasSelectedField, editing, modifying]) => {
      if (modifying) {
        return 'save';
      } else if (editing) {
        return 'clear';
      } else if (hasSelectedField) {
        return 'send';
      } else {
        return 'add';
      }
    }),
  );

  primaryActionText$ = this.primaryAction$.pipe(
    map(([hasSelectedField, editing, modifying]) => {
      if (modifying) {
        return 'save';
      } else if (editing) {
        return 'cancel';
      } else if (hasSelectedField) {
        return 'sendField';
      } else {
        return 'addField';
      }
    }),
  );

  secondaryActionIcon$ = combineLatest([this.customerProgress$, this.cropFieldType$]).pipe(
    map(([customerProgress, cropFieldType]) => {
      if (!customerProgress) {
        return '';
      }

      if (cropFieldType === CropFieldType.Advice) {
        if (customerProgress.processes.fieldPlanCropFieldsCompleted) {
          return 'check_box';
        } else {
          return 'check_box_outline_blank';
        }
      } else {
        if (customerProgress.processes.fieldPlanManurespaceCompleted) {
          return 'check_box';
        } else {
          return 'check_box_outline_blank';
        }
      }
    }),
  );

  secondaryActionText$ = this.cropFieldType$.pipe(
    map((cropFieldType) =>
      cropFieldType === CropFieldType.Advice ? 'customerProgressAdvice' : 'customerProgressManure',
    ),
  );

  interactionType$ = this.store.pipe(select(getInteractionType));

  isInteractionTypeSelectCropField$ = this.interactionType$.pipe(
    map(
      (interactionType) =>
        interactionType === InteractionType.selectCropField ||
        interactionType === InteractionType.synchronizeCropFieldsWithBMS ||
        interactionType === InteractionType.synchronizeCropFieldsWithRvO,
    ),
  );

  pageTitle$ = combineLatest([
    this.store.pipe(select(getSelectedFeaturesLength)),
    this.interactionType$,
    this.store.pipe(select(UserSelectors.selectCustomer)),
  ]).pipe(
    map(([amount, interactionType, customer]) => {
      switch (interactionType) {
        case InteractionType.linkSoilAnalysisToCropField:
          return this.locale.translate('selectCropField');
        case InteractionType.selectSoilAnalysis:
          return `${this.locale.translate('soilAnalyses')} ${
            customer ? `${customer.name} ${customer.externalId}` : ''
          }`;
        case InteractionType.drawFeature:
          return this.locale.translate('drawCropField');
        case InteractionType.drawDivision:
          return this.locale.translate('drawDivision');
        case InteractionType.drawOverlap:
          return this.locale.translate('drawOverlap');
        case InteractionType.modifyFeature:
          return this.locale.translate('editCropField');
        case InteractionType.cloneRvo:
          return this.locale.translate('cloneRvoField');
        case InteractionType.cloneFieldFromLastYear:
          return this.locale.translate('cloneFieldFromLastYear');
        case InteractionType.copyShapefileData:
          return this.locale.translate('copyShapefileField');
        case InteractionType.selectCropField:
          return amount === 0
            ? `${this.locale.translate('Fields')} ${
                customer ? `${customer.name} ${customer.externalId}` : ''
              }`
            : `${amount} ${this.locale.translate(
                amount === 1 ? 'fieldSelected' : 'fieldsSelected',
              )}`;
        default:
          return '';
      }
    }),
  );

  buttonItems$ = combineLatest([
    this.isEditing$,
    this.isMobile$,
    this.hasSelectedField$,
    this.hasSelectedSingleField$,
    this.hasSelectedTwoFields$,
    this.cropFieldType$,
    this.hasSelectedCropFieldsParticipatingInBem$,
  ]).pipe(
    map(
      ([
        editing,
        isMobile,
        hasSelectedField,
        hasSelectedSingleField,
        hasSelectedTwoFields,
        cropFieldType,
        hasSelectedCropFieldsParticipatingInBem,
      ]: [boolean, boolean, boolean, boolean, boolean, CropFieldType, boolean]) => {
        const optionSalesPotential = {
          icon: 'insights',
          viewValue: this.locale.translate('salesPotential.title'),
          key: ButtonAction.viewSalesPotential,
        };
        const optionFmsDiagnose = {
          icon: null as string,
          svgIcon: 'cruelty_free',
          viewValue: this.locale.translate('fmsDiagnose.title'),
          key: ButtonAction.fmsDiagnose,
        };

        if (!editing) {
          const isAdviceMode = cropFieldType === CropFieldType.Advice;
          const isManureSpaceMode = cropFieldType === CropFieldType.ManureSpace;

          const optionsExactlyOne = [
            {
              icon: 'edit',
              viewValue: this.locale.translate('edit'),
              key: ButtonAction.edit,
            },
            {
              icon: 'file_copy',
              viewValue: this.locale.translate('copy'),
              key: ButtonAction.clone,
            },
          ];
          const optionsAtLeastOne = [
            {
              icon: 'delete',
              viewValue: this.locale.translate('remove'),
              key: ButtonAction.remove,
              disabled: hasSelectedCropFieldsParticipatingInBem,
              tooltipText: hasSelectedCropFieldsParticipatingInBem
                ? this.locale.translate('deleteButtonDisabledTooltip')
                : undefined,
            },
            {
              icon: 'analytics',
              viewValue: this.locale.translate('soilAnalyses'),
              key: ButtonAction.soilAnalysesReport,
            },
          ];
          const optionDraw = {
            icon: null as string,
            svgIcon: 'vector_polyline',
            viewValue: this.locale.translate('draw'),
            key: ButtonAction.addOrEdit,
          };
          const optionCut = {
            icon: 'content_cut',
            viewValue: this.locale.translate('divide'),
            key: ButtonAction.divide,
          };
          const optionOverview = {
            icon: 'list_alt',
            viewValue: this.locale.translate('overview'),
            key: ButtonAction.overview,
          };
          const optionListFields = {
            icon: 'list',
            viewValue: this.locale.translate('cropFieldList'),
            key: ButtonAction.cropFieldList,
          };
          const optionCopyFromCultivationPlan = {
            icon: 'file_copy',
            viewValue: this.locale.translate('copyFieldsFromCultivationPlan'),
            key: ButtonAction.copyFieldsFromCultivationPlan,
          };
          const optionUpdateNvg = {
            icon: 'edit',
            viewValue: this.locale.translate('nvg-update-button-title'),
            key: ButtonAction.updateNvg,
          };
          const optionCombineFields = {
            icon: 'layers',
            viewValue: this.locale.translate('combineFields'),
            key: ButtonAction.combine,
          };
          const optionSubsequentCultivation = {
            icon: 'refresh',
            viewValue: this.locale.translate('subsequentCultivation'),
            key: ButtonAction.subsequentCultivation,
          };
          const optionMatchingDepth = {
            icon: 'height',
            viewValue: this.locale.translate('machiningDepth'),
            key: ButtonAction.editMachiningDepth,
          };

          // 0 selected fields
          if (!hasSelectedField) {
            return [
              optionOverview,
              optionUpdateNvg,
              optionSalesPotential,
              ...(isMobile ? [optionListFields] : []),
              ...(isManureSpaceMode ? [optionCopyFromCultivationPlan] : []),
              ...(this.env.SHOW_FMS_DIAGNOSE_BUTTON ? [optionFmsDiagnose] : []),
            ];

            // 2 selected fields
          } else if (hasSelectedTwoFields) {
            return [
              ...(isAdviceMode ? [optionCombineFields] : []),
              ...optionsAtLeastOne,
              optionMatchingDepth,
            ];

            // 1 selected field
          } else if (hasSelectedSingleField) {
            return [
              ...(isAdviceMode ? [optionDraw] : []),
              ...(isAdviceMode ? [optionCut] : []),
              ...optionsExactlyOne,
              ...optionsAtLeastOne,
              optionSubsequentCultivation,
            ];

            // More than 2 selected fields
          } else {
            return [...optionsAtLeastOne, optionMatchingDepth];
          }

          // Options while in editing mode:
        } else {
          return [];
        }
      },
    ),
  );

  selectItems$: Observable<SelectItem<unknown>[]> = combineLatest([
    this.store.pipe(select(getCropFieldType)),
    this.store.pipe(select(getAvailableCropYears)),
    this.store.pipe(select(getCropYear)),
    this.isEditing$,
    this.hasSelectedField$,
  ]).pipe(
    map(([cropFieldType, availableYears, year, editing, hasSelectedField]) => {
      const manureSpaceSelect = !this.env.ENABLE_MANURE_SPACE
        ? []
        : [
            {
              icon: 'calendar_today',
              viewValue: this.locale.translate('mode'),
              key: SelectAction.cropFieldType,
              value: cropFieldType,
              items: [
                {
                  value: CropFieldType.Advice,
                  viewValue: this.locale.translate('cultivationPlan'),
                },
                {
                  value: CropFieldType.ManureSpace,
                  viewValue: this.locale.translate('manureSpace'),
                },
              ],
            },
          ];
      const yearSelect = {
        icon: 'calendar_today',
        viewValue: this.locale.translate('year'),
        key: SelectAction.year,
        value: year,
        items: availableYears.map((availableYear) => ({
          value: availableYear,
          viewValue: availableYear.toString(),
        })),
      };
      return editing || hasSelectedField ? [] : [...manureSpaceSelect, yearSelect];
    }),
  );
  mapColors = MapColors;

  private readonly fieldsLayerFormControl = new FormControl({ value: '', disabled: true });
  private readonly soilAnalysesLayerFormControl = new FormControl({ value: '', disabled: true });
  private readonly rvoLayerFormControl = new FormControl('');

  layerMenuItems: LayerMenuItem[] = [
    {
      key: 'fieldsLayer',
      label: 'fieldsLayer',
      formControl: this.fieldsLayerFormControl,
      selector: getCropFieldsEnabled,
      action: (val: boolean) => new SetCropFieldsEnabled(val),
      layers: [{ label: 'Fields', color: this.mapColors.viewStroke }],
      colors: [this.mapColors.viewStroke],
    },
    {
      key: 'blightLayer',
      label: 'blight.mapLayerTitle',
      formControl: new FormControl({ value: true, disabled: false }),
      action: (val: boolean) => new SetBlightLayerEnabled(val),
      selector: getBlightLayerEnabled,
      layers: [
        {
          label: 'blight.mapLayerTitle',
          color: MapColors.blightPreventiveStroke,
        },
      ],
      layerOpacity: 0.8,
      colors: [MapColors.blightPreventiveStroke],
    },
    {
      key: 'soilAnalysesLayer',
      label: 'soilAnalysesLayer',
      formControl: this.soilAnalysesLayerFormControl,
      selector: getSoilAnalysesEnabled,
      action: (val: boolean) => new SetSoilAnalysesEnabled(val),
      layers: [{ label: 'soilAnalysis', color: this.mapColors.soilAnalysesStroke }],
      colors: [this.mapColors.soilAnalysesStroke],
    },
    {
      key: 'rvoLayer',
      label: 'rvoLayer',
      formControl: this.rvoLayerFormControl,
      selector: getRvoEnabled,
      action: (val: boolean) => new SetRvoEnabled(val),
      layers: [{ label: 'rvoLayer', color: this.mapColors.rvoStroke }],
      colors: [this.mapColors.rvoStroke],
    },
  ];

  private readonly subscription = new Subscription();
  private currentInteractionType: InteractionType;

  ngOnInit() {
    this.subscription.add(
      this.interactionType$.subscribe((interactionType) => {
        this.currentInteractionType = interactionType;
        if (this.tabGroup) {
          const index = this.tabGroup.selectedIndex;
          if (interactionType === InteractionType.selectCropField && index !== 0) {
            this.tabGroup.selectedIndex = 0;
          }
          if (interactionType === InteractionType.selectSoilAnalysis && index !== 1) {
            this.tabGroup.selectedIndex = 1;
          }
        }

        if (
          interactionType === InteractionType.selectCropField ||
          interactionType === InteractionType.linkSoilAnalysisToCropField
        ) {
          this.store.dispatch(new ApplySavedLayers());
        }
        this.geoService.setInteraction(interactionType);
      }),
    );

    //Get data for blight warnings layer
    const dates = buildBlightRequestedDates(defaultBlightDayRange);
    const requestBlightCropFields = new RequestBlightCropFieldsByDateRange(dates[0], dates[1]);
    this.store.dispatch({
      ...requestBlightCropFields,
      startDate: dates[0].toISOString(),
      endDate: dates[1].toISOString(),
    });
    this.store.dispatch(new LoadCustomerBlightRights());

    this.updateChangingValues();
    this.updateDisabledStatus();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  updateChangingValues() {
    this.layerMenuItems.forEach((menuItem) => {
      if (menuItem.formControl && menuItem.action) {
        this.subscription.add(
          menuItem.formControl.valueChanges.subscribe((value) => {
            // @ts-ignore: allow menuItem.action to be used as a constructor
            this.store.dispatch(menuItem.action(value));
          }),
        );
      }
    });
  }

  updateDisabledStatus() {
    const params = { onlySelf: true, emitEvent: false };

    this.subscription.add(
      this.store.pipe(select(isSelectingCropField)).subscribe((isCloning) => {
        if (isCloning) {
          this.fieldsLayerFormControl.disable(params);
        } else {
          this.fieldsLayerFormControl.enable(params);
        }
      }),
    );
    this.subscription.add(
      this.store.pipe(select(isSelectingSoilAnalysis)).subscribe((isCloning) => {
        if (isCloning) {
          this.soilAnalysesLayerFormControl.disable(params);
        } else {
          this.soilAnalysesLayerFormControl.enable(params);
        }
      }),
    );
    this.subscription.add(
      this.store.pipe(select(isCloningRvo)).subscribe((isCloning) => {
        if (isCloning) {
          this.rvoLayerFormControl.disable(params);
        } else {
          this.rvoLayerFormControl.enable(params);
        }
      }),
    );
  }

  buttonAction(data: string) {
    switch (data) {
      case ButtonAction.addOrEdit:
        this.addOrEdit();
        break;
      case ButtonAction.edit:
        this.edit();
        break;
      case ButtonAction.divide:
        this.divide();
        break;
      case ButtonAction.remove:
        this.remove();
        break;
      case ButtonAction.clone:
        this.clone();
        break;
      case ButtonAction.cropFieldList:
        this.setSearch();
        break;
      case ButtonAction.overview:
        this.openOverview();
        break;
      case ButtonAction.subsequentCultivation:
        this.subsequentCultivation();
        break;
      case ButtonAction.combine:
        this.combineFields();
        break;
      case ButtonAction.soilAnalysesReport:
        this.openSoilAnalysesReport();
        break;
      case ButtonAction.saveModification:
        this.saveModification();
        break;
      case ButtonAction.editMachiningDepth:
        this.editMachiningDepth();
        break;
      case ButtonAction.copyFieldsFromCultivationPlan:
        this.copyFieldsFromCultivationPlan();
        break;
      case ButtonAction.updateNvg:
        this.updateNvgValues();
        break;
      case ButtonAction.viewSalesPotential:
        this.store.dispatch(new ViewSalesPotential());
        break;
      case ButtonAction.fmsDiagnose:
        this.store.dispatch(new OpenFmsDiagnose());
        break;
    }
  }

  selectAction([action, value]: [string, unknown]) {
    switch (action) {
      case SelectAction.cropFieldType:
        this.store.dispatch(new ResetCropFieldType(value as CropFieldType));
        break;
      case SelectAction.year:
        this.store.dispatch(new ResetCropYear(value as number));
        break;
    }
  }

  primaryAction() {
    this.primaryAction$.pipe(take(1)).subscribe(([hasSelectedField, editing, modifying]) => {
      if (modifying) {
        this.saveModification();
      } else if (editing) {
        if (this.currentInteractionType === InteractionType.cloneRvo) {
          this.store.dispatch(new SetRvoEnabled(false));
        }
        this.cancelDraw();
      } else if (hasSelectedField) {
        this.send();
      } else {
        this.addOrEdit();
      }
    });
  }

  secondaryAction() {
    this.cropFieldType$.pipe(take(1)).subscribe((cropFieldType) => {
      if (cropFieldType === CropFieldType.Advice) {
        this.customerProgressAdviceToggle();
      } else {
        this.customerProgressManureToggle();
      }
    });
  }

  private send() {
    this.store.dispatch(new SendSelectedCropFields());
  }

  private addOrEdit() {
    this.store.dispatch(new AddOrEditCropField());
  }

  deselect() {
    this.store.dispatch(new DeselectAllFields());
    this.store.dispatch(SoilAnalysisActions.deselectAllSoilAnalysisFeatures());
  }

  cancel() {
    if (this.currentInteractionType === InteractionType.cloneRvo) {
      this.store.dispatch(new SetRvoEnabled(false));
    }
    this.store.dispatch(new SetInteractionType(InteractionType.selectCropField));
    this.deselect();
  }

  tabChange(event: MatTabChangeEvent) {
    if (event.index === 0) {
      this.store.dispatch(new SetInteractionType(InteractionType.selectCropField));
    } else if (event.index === 1) {
      this.store.dispatch(new DeselectInteractionFeatures());
      this.store.dispatch(new SetInteractionType(InteractionType.selectSoilAnalysis));
    }
  }

  quickEditCropFields() {
    this.store.dispatch(new QuickEditCropFields());
  }

  customerProgressAdviceToggle() {
    this.store.dispatch(new ToggleCustomerProgressAdvice());
  }

  customerProgressManureToggle() {
    this.store.dispatch(new ToggleCustomerProgressManure());
  }

  private clone() {
    this.store.dispatch(new CloneSelectedCropField());
  }

  private edit() {
    this.store.dispatch(new EditSelectedCropFields());
  }

  private divide() {
    this.store.dispatch(new DivideField());
    this.store.dispatch(new SetPopupEnabledStatus(false));
  }

  private remove() {
    this.store.dispatch(new DeleteCropFields());
  }

  private setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  private cancelDraw() {
    this.store.dispatch(new SetInteractionType(InteractionType.selectCropField));
    this.store.dispatch(new DeselectAllFields());
    this.store.dispatch(new SetPopupEnabledStatus(true));
  }

  private openOverview() {
    this.store.dispatch(new OpenOverview());
  }

  private subsequentCultivation() {
    this.store.dispatch(new CreateSubsequentCultivation());
  }

  private combineFields() {
    this.store.dispatch(new CombineFields());
  }

  private openSoilAnalysesReport() {
    this.store.dispatch(new OpenSoilAnalysesReportForSelectedCropFields());
  }

  private saveModification() {
    this.store.dispatch(new InteractionFeatureModified());
  }

  private editMachiningDepth() {
    this.store.dispatch(new EditMachiningDepth());
  }

  private copyFieldsFromCultivationPlan() {
    this.store.dispatch(new ShowCopyFieldsToManureSpaceDialog());
  }

  private updateNvgValues() {
    this.store.dispatch(new OpenNvgConfirmationDialog());
  }
}
